import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Modal, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ChevronDown, ChevronUp, Grid3X3, Search } from 'lucide-react';
import {
  ASSIGNEE_TYPES,
  EDIT_PERMISSION_ORDER,
  EDIT_PERMISSIONS,
  PERMISSION_TYPES,
  VIEW_PERMISSIONS,
} from '../constants';
import { useDashboardData } from '../context';
import { useDismissModalOnRouteChange } from '../hooks';
import {
  asArray,
  DEFAULT_VIEW_PERMISSIONS,
  normalizeAssignment,
  toSharedPayload,
} from '../utils/shareDashboardAssignmentUtils';
import DashboardButton from './DashboardButton';
import ShareAssignmentRow from './ShareAssignmentRow';
import ShareConfirmationStep from './ShareConfirmationStep';

const ShareDashboardModal = ({
  open = false,
  onClose,
  onConfirm,
  dashboardId = 'new',
  dashboardName = '',
  existingSharedWith = [],
}) => {
  const { t } = useTranslation('dashboards');
  const { shareCandidates, loadingShareCandidates, fetchShareCandidates, shareDashboard } = useDashboardData();
  const editPermissionLabels = useMemo(
    () => ({
      [EDIT_PERMISSIONS.MOVE]: t('share.modal.permissions.move', { defaultValue: 'Move widgets' }),
      [EDIT_PERMISSIONS.RESIZE]: t('share.modal.permissions.resize', { defaultValue: 'Resize widgets' }),
      [EDIT_PERMISSIONS.RENAME]: t('share.modal.permissions.rename', { defaultValue: 'Change name of widgets' }),
      [EDIT_PERMISSIONS.CONFIGURE]: t('share.modal.permissions.configure', { defaultValue: 'Configure widgets' }),
      [EDIT_PERMISSIONS.ADD_WIDGET]: t('share.modal.permissions.createItem', { defaultValue: 'Add new widgets' }),
    }),
    [t],
  );

  const getAssignmentSummary = useCallback(
    (entry) => {
      const normalized = normalizeAssignment(entry);
      const hasEdit = normalized.permissionModes.includes(PERMISSION_TYPES.EDIT);
      const hasCreate = normalized.permissionModes.includes(PERMISSION_TYPES.CREATE);
      const viewExtras = normalized.viewPermissions.filter((key) => key !== VIEW_PERMISSIONS.VIEW_ONLY).length;
      const editExtras = normalized.editPermissions.length;
      const extrasCount = viewExtras + editExtras + (hasCreate ? 1 : 0);
      const extras = extrasCount > 0 ? `, + ${extrasCount}` : '';

      if (!hasEdit && extrasCount === 0) return t('share.modal.viewSummary');
      if (hasEdit) return t('share.modal.canEditView', { extras });
      return t('share.modal.canView', { extras });
    },
    [t],
  );

  const getAssignmentPermissionDetailLines = useCallback(
    (entry) => {
      const n = normalizeAssignment(entry);
      const lines = [];
      const modes = asArray(n.permissionModes);
      const vp = asArray(n.viewPermissions);
      const ep = asArray(n.editPermissions);

      if (modes.includes(PERMISSION_TYPES.VIEW) && vp.includes(VIEW_PERMISSIONS.VIEW_ONLY)) {
        lines.push(t('share.modal.permissions.viewOnly'));
      }
      if (vp.includes(VIEW_PERMISSIONS.EXPORT_DATA)) {
        lines.push(t('share.modal.permissions.viewExport'));
      }
      if (modes.includes(PERMISSION_TYPES.EDIT)) {
        lines.push(t('share.confirmation.accessEdit'));
        EDIT_PERMISSION_ORDER.forEach((key) => {
          if (ep.includes(key)) lines.push(editPermissionLabels[key]);
        });
      }
      if (modes.includes(PERMISSION_TYPES.CREATE)) {
        lines.push(t('share.modal.permissions.createItem'));
      }

      return lines.length ? lines : [getAssignmentSummary(entry)];
    },
    [t, editPermissionLabels, getAssignmentSummary],
  );

  const getAssignmentExtrasCount = useCallback((entry) => {
    const n = normalizeAssignment(entry);
    const hasCreate = n.permissionModes.includes(PERMISSION_TYPES.CREATE);
    const viewExtras = n.viewPermissions.filter((key) => key !== VIEW_PERMISSIONS.VIEW_ONLY).length;
    const editExtras = n.editPermissions.length;
    return viewExtras + editExtras + (hasCreate ? 1 : 0);
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [stagedSelections, setStagedSelections] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [existingEntries, setExistingEntries] = useState(() => asArray(existingSharedWith).map(normalizeAssignment));
  const [isAccessExpanded, setIsAccessExpanded] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [openPermissionsFor, setOpenPermissionsFor] = useState(null);

  useEffect(() => {
    if (!open) return;
    fetchShareCandidates();
  }, [fetchShareCandidates, open]);

  useEffect(() => {
    if (!open) return;
    setSearchTerm('');
    setStagedSelections([]);
    setAssignments([]);
    setShowConfirmation(false);
    setConfirming(false);
    setOpenPermissionsFor(null);
    setIsAccessExpanded(true);
    setExistingEntries(asArray(existingSharedWith).map(normalizeAssignment));
  }, [dashboardId, existingSharedWith, open]);

  useDismissModalOnRouteChange(open, onClose);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const blockedIds = useMemo(() => {
    return new Set([
      ...stagedSelections.map((item) => item.id),
      ...assignments.map((item) => item.id),
      ...existingEntries.map((item) => item.id),
    ]);
  }, [assignments, existingEntries, stagedSelections]);

  const filteredCandidates = useMemo(() => {
    if (!normalizedSearch) return [];
    return shareCandidates
      .filter((item) => {
        const matchText = `${item.name || ''} ${item.role || ''}`.toLowerCase();
        return matchText.includes(normalizedSearch) && !blockedIds.has(item.id);
      })
      .slice(0, 8);
  }, [blockedIds, normalizedSearch, shareCandidates]);

  const addStagedSelection = useCallback((candidate) => {
    if (!candidate?.id) return;
    setStagedSelections((prev) => {
      if (prev.some((item) => item.id === candidate.id)) return prev;
      return [...prev, candidate];
    });
    setSearchTerm('');
  }, []);

  const removeStagedSelection = useCallback((id) => {
    setStagedSelections((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const commitStagedSelections = useCallback(() => {
    if (!stagedSelections.length) return;
    setAssignments((prev) => {
      const existingIds = new Set(prev.map((item) => item.id));
      const next = [...prev];
      stagedSelections.forEach((item) => {
        if (existingIds.has(item.id)) return;
        existingIds.add(item.id);
        next.push(
          normalizeAssignment({
            ...item,
            permissionLevel: PERMISSION_TYPES.VIEW,
            permissionModes: [PERMISSION_TYPES.VIEW],
            editPermissions: [],
            viewPermissions: DEFAULT_VIEW_PERMISSIONS,
          }),
        );
      });
      return next;
    });
    setStagedSelections([]);
    setSearchTerm('');
  }, [stagedSelections]);

  const removeAssignment = useCallback((id) => {
    setAssignments((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const removeExistingAssignment = useCallback((id) => {
    setExistingEntries((prev) => prev.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    if (!openPermissionsFor) return;
    const handleOutsideClick = (event) => {
      if (event.target?.closest?.('[data-permission-root="true"]')) return;
      setOpenPermissionsFor(null);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [openPermissionsFor]);

  const updatePermission = useCallback((id, updater, source = 'staged') => {
    const apply = (prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const next = typeof updater === 'function' ? updater(item) : { ...item, ...updater };
        return normalizeAssignment(next);
      });

    if (source === 'existing') setExistingEntries(apply);
    else setAssignments(apply);
  }, []);

  const toggleMode = useCallback(
    (id, mode, source = 'staged') => {
      updatePermission(
        id,
        (item) => {
          const nextModes = new Set(asArray(item.permissionModes));
          if (mode === PERMISSION_TYPES.VIEW) nextModes.add(PERMISSION_TYPES.VIEW);
          else if (nextModes.has(mode)) nextModes.delete(mode);
          else nextModes.add(mode);

          if (!nextModes.has(PERMISSION_TYPES.VIEW)) nextModes.add(PERMISSION_TYPES.VIEW);

          const hasEdit = nextModes.has(PERMISSION_TYPES.EDIT);

          return {
            ...item,
            permissionModes: Array.from(nextModes),
            permissionLevel: hasEdit ? PERMISSION_TYPES.EDIT : PERMISSION_TYPES.VIEW,
            editPermissions: hasEdit ? item.editPermissions : [],
            viewPermissions: item.viewPermissions?.length ? item.viewPermissions : DEFAULT_VIEW_PERMISSIONS,
          };
        },
        source,
      );
    },
    [updatePermission],
  );

  const toggleEditPermission = useCallback(
    (id, permissionKey, source = 'staged') => {
      updatePermission(
        id,
        (item) => {
          const next = new Set(asArray(item.editPermissions));
          if (next.has(permissionKey)) next.delete(permissionKey);
          else next.add(permissionKey);
          return {
            ...item,
            permissionModes: Array.from(
              new Set([...asArray(item.permissionModes), PERMISSION_TYPES.VIEW, PERMISSION_TYPES.EDIT]),
            ),
            permissionLevel: PERMISSION_TYPES.EDIT,
            editPermissions: Array.from(next),
          };
        },
        source,
      );
    },
    [updatePermission],
  );

  const toggleViewPermission = useCallback(
    (id, permissionKey, source = 'staged') => {
      updatePermission(
        id,
        (item) => {
          const next = new Set(asArray(item.viewPermissions));
          if (permissionKey === VIEW_PERMISSIONS.VIEW_ONLY) {
            next.add(VIEW_PERMISSIONS.VIEW_ONLY);
          } else if (next.has(permissionKey)) {
            next.delete(permissionKey);
          } else {
            next.add(permissionKey);
          }
          next.add(VIEW_PERMISSIONS.VIEW_ONLY);
          return { ...item, viewPermissions: Array.from(next) };
        },
        source,
      );
    },
    [updatePermission],
  );

  const normalizedAssignments = useMemo(() => assignments.map(normalizeAssignment), [assignments]);
  const stagedCounts = useMemo(() => {
    return normalizedAssignments.reduce(
      (acc, item) => {
        if (item.type === ASSIGNEE_TYPES.USER) acc.users += 1;
        if (item.type === ASSIGNEE_TYPES.DEPARTMENT) acc.departments += 1;
        return acc;
      },
      { users: 0, departments: 0 },
    );
  }, [normalizedAssignments]);

  const renderConfirmationCanDo = useCallback(
    (entry) => {
      const n = normalizeAssignment(entry);
      const hasView = n.permissionModes.includes(PERMISSION_TYPES.VIEW);
      const hasEdit = n.permissionModes.includes(PERMISSION_TYPES.EDIT);
      const extras = getAssignmentExtrasCount(entry);
      const detailLines = getAssignmentPermissionDetailLines(entry);

      return (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {hasView && (
            <Badge variant="secondary" size="small">
              {t('share.confirmation.accessView')}
            </Badge>
          )}
          {hasEdit && (
            <Badge variant="informative" size="small">
              {t('share.confirmation.accessEdit')}
            </Badge>
          )}
          {extras > 0 && (
            <Tooltip
              position="top"
              maxWidth="280px"
              content={
                <ul className="m-0 list-disc space-y-1 py-0.5 pl-4 text-left">
                  {detailLines.map((line, idx) => (
                    <li key={`${idx}-${line}`}>{line}</li>
                  ))}
                </ul>
              }
            >
              <span className="inline-flex cursor-default">
                <Badge variant="neutral" size="small">{`+ ${extras}`}</Badge>
              </span>
            </Tooltip>
          )}
        </div>
      );
    },
    [t, getAssignmentExtrasCount, getAssignmentPermissionDetailLines],
  );

  const handleOpenConfirmation = useCallback(() => {
    if (!normalizedAssignments.length) return;
    setShowConfirmation(true);
  }, [normalizedAssignments.length]);

  const handleBackFromConfirmation = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const handleConfirmShare = useCallback(async () => {
    setConfirming(true);
    try {
      const payload = normalizedAssignments.map(toSharedPayload);
      if (dashboardId && dashboardId !== 'new') {
        await shareDashboard(dashboardId, payload);
      }
      const merged = [...existingEntries, ...payload];
      onConfirm?.(merged);
      onClose?.();
    } finally {
      setConfirming(false);
    }
  }, [dashboardId, existingEntries, normalizedAssignments, onClose, onConfirm, shareDashboard]);

  const handleCopyLink = useCallback(async () => {
    const currentUrl = window?.location?.href;
    if (!currentUrl) return;
    try {
      await navigator.clipboard.writeText(currentUrl);
    } catch (error) {
      console.warn('Could not copy link', error);
    }
  }, []);

  const accessCountLabel = t('share.modal.accessInfoCount', { count: existingEntries.length });

  if (!open) return null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={showConfirmation ? t('share.confirmation.title') : t('share.modal.title')}
      size="lg"
      portalRootId="shell-main-content"
      className="!max-w-[770px]"
    >
      {showConfirmation ? (
        <ShareConfirmationStep
          dashboardName={dashboardName}
          stagedCounts={stagedCounts}
          normalizedAssignments={normalizedAssignments}
          renderConfirmationCanDo={renderConfirmationCanDo}
          onBack={handleBackFromConfirmation}
          onConfirm={handleConfirmShare}
          confirming={confirming}
        />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-content-secondary">
            <Grid3X3 size={14} />
            <span className="truncate">{dashboardName || t('common.dashboardNameFallback')}</span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <div className="flex min-h-9 flex-wrap items-center gap-1 rounded-md border border-edge-strong bg-surface px-2 py-1">
                  <Search size={14} className="text-content-muted" />
                  {stagedSelections.map((item) => (
                    <Badge
                      key={`staged-${item.id}`}
                      variant="neutral"
                      size="small"
                      closable
                      closeButtonAriaLabel={t('share.modal.removeAssigneeAria')}
                      onClose={() => removeStagedSelection(item.id)}
                    >
                      {item.name}
                    </Badge>
                  ))}
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder={t('share.modal.searchPlaceholder')}
                    className="min-w-[180px] flex-1 border-0 bg-transparent text-xs outline-none placeholder:text-content-faint"
                  />
                </div>
                {normalizedSearch && (
                  <div className="scrollbar-aimsos-auto-sm absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-edge bg-surface shadow-lg">
                    {loadingShareCandidates ? (
                      <p className="px-3 py-2 text-xs text-content-muted">{t('common.loading')}</p>
                    ) : filteredCandidates.length === 0 ? (
                      <p className="px-3 py-2 text-xs text-content-muted">{t('share.modal.noResults')}</p>
                    ) : (
                      filteredCandidates.map((item) => (
                        <button
                          key={`candidate-${item.id}`}
                          type="button"
                          onClick={() => addStagedSelection(item)}
                          className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-surface-hover-subtle"
                        >
                          <span className="text-xs text-content">{item.name}</span>
                          <span className="text-[11px] text-content-muted">
                            {item.type === ASSIGNEE_TYPES.USER
                              ? item.role || t('common.userFallbackRole')
                              : t('common.members', { count: item.userCount || item.membersCount || 0 })}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <DashboardButton size="md" onClick={commitStagedSelections} disabled={!stagedSelections.length}>
                {t('share.modal.addButton')}
              </DashboardButton>
            </div>
          </div>

          {normalizedAssignments.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-content">{t('share.modal.toBeAddedTitle')}</p>
              <p className="text-xs text-content-secondary">
                {t('share.modal.toBeAddedCount', { users: stagedCounts.users, departments: stagedCounts.departments })}
              </p>
              <div className="space-y-2">
                {normalizedAssignments.map((item) => (
                  <ShareAssignmentRow
                    key={`staged-${item.id}`}
                    item={item}
                    source="staged"
                    isPermissionsOpen={openPermissionsFor === `staged:${item.id}`}
                    onTogglePanel={() =>
                      setOpenPermissionsFor((prev) => (prev === `staged:${item.id}` ? null : `staged:${item.id}`))
                    }
                    editPermissionLabels={editPermissionLabels}
                    getAssignmentSummary={getAssignmentSummary}
                    getAssignmentPermissionDetailLines={getAssignmentPermissionDetailLines}
                    onToggleEditPermission={(permissionKey) => toggleEditPermission(item.id, permissionKey, 'staged')}
                    onToggleViewPermission={(permissionKey) => toggleViewPermission(item.id, permissionKey, 'staged')}
                    onToggleCreateMode={() => toggleMode(item.id, PERMISSION_TYPES.CREATE, 'staged')}
                    onRemove={() => removeAssignment(item.id)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2 border-t border-edge pt-2">
            <button
              type="button"
              onClick={() => setIsAccessExpanded((prev) => !prev)}
              className="flex w-full items-center justify-between text-left"
            >
              <div>
                <p className="text-xs font-semibold text-content">{t('share.modal.accessInfoTitle')}</p>
                <p className="text-xs text-content-secondary">{accessCountLabel}</p>
              </div>
              {isAccessExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {isAccessExpanded && existingEntries.length > 0 && (
              <div className="space-y-2">
                {existingEntries.map((item) => (
                  <ShareAssignmentRow
                    key={`existing-${item.id}`}
                    item={item}
                    source="existing"
                    isPermissionsOpen={openPermissionsFor === `existing:${item.id}`}
                    onTogglePanel={() =>
                      setOpenPermissionsFor((prev) =>
                        prev === `existing:${item.id}` ? null : `existing:${item.id}`,
                      )
                    }
                    editPermissionLabels={editPermissionLabels}
                    getAssignmentSummary={getAssignmentSummary}
                    getAssignmentPermissionDetailLines={getAssignmentPermissionDetailLines}
                    onToggleEditPermission={(permissionKey) =>
                      toggleEditPermission(item.id, permissionKey, 'existing')
                    }
                    onToggleViewPermission={(permissionKey) =>
                      toggleViewPermission(item.id, permissionKey, 'existing')
                    }
                    onToggleCreateMode={() => toggleMode(item.id, PERMISSION_TYPES.CREATE, 'existing')}
                    onRemove={() => removeExistingAssignment(item.id)}
                  />
                ))}
              </div>
            )}
          </div>

          <Modal.Footer className="!px-0 !pb-0">
            <DashboardButton variant="secondary" onClick={handleCopyLink}>
              {t('share.modal.copyLinkButton')}
            </DashboardButton>
            <DashboardButton onClick={handleOpenConfirmation} disabled={!normalizedAssignments.length}>
              {t('share.modal.shareButton')}
            </DashboardButton>
          </Modal.Footer>
        </div>
      )}
    </Modal>
  );
};

export default memo(ShareDashboardModal);
